import { useMemo } from 'react';
import { Tooltip } from 'antd';
import {
  ANTI_DETECTION_SIGNAL_CATALOG,
  type AntiDetectionSignalKey,
  type AntiDetectionSignalSpec,
  correlationColor,
  correlationBand,
} from '../../shared/anti-detection/signals';

/**
 * Sprint 25 / S25-M — 16×16 SVG correlation heatmap.
 *
 * Reuses the T1-E "no new dependency" pattern: a plain SVG grid that scales
 * to the container width. Each cell is a 30×30 rect coloured by the
 * `correlationColor()` band; cells with |r| > 0.7 carry a thin white border
 * so the strong cluster is visible at-a-glance even in dark theme.
 *
 * Click handler:
 *   - Off-diagonal cells: fires `onCellClick(row, col)` so the parent page
 *     can route to the drill-down sub-view.
 *   - Diagonal cells: no-op (correlation of a signal with itself is always
 *     1 for live signals, 0 for isolated ones — no useful drill-down).
 */
export interface CorrelationCell {
  row: AntiDetectionSignalKey;
  col: AntiDetectionSignalKey;
  r: number;
  strong: boolean;
}

export interface CorrelationHeatmapProps {
  cells: CorrelationCell[];
  onCellClick?: (
    row: AntiDetectionSignalKey,
    col: AntiDetectionSignalKey,
    r: number
  ) => void;
  /** Highlighted axis index (row + col); used for hover cross-hair. */
  highlightedIndex?: number | null;
  onHighlightChange?: (idx: number | null) => void;
}

const CELL_SIZE = 30;
const AXIS_GUTTER = 200; // px reserved for the label column / row
const SIDE = ANTI_DETECTION_SIGNAL_CATALOG.length; // = 16

export const CorrelationHeatmap: React.FC<CorrelationHeatmapProps> = ({
  cells,
  onCellClick,
  highlightedIndex,
  onHighlightChange,
}) => {
  const lookup = useMemo(() => {
    const m = new Map<string, CorrelationCell>();
    for (const c of cells) m.set(`${c.row}|${c.col}`, c);
    return m;
  }, [cells]);

  const gridWidth = AXIS_GUTTER + SIDE * CELL_SIZE + 16;
  const gridHeight = AXIS_GUTTER + SIDE * CELL_SIZE + 16;

  return (
    <svg
      data-testid="anti-detection-correlation-heatmap"
      width={gridWidth}
      height={gridHeight}
      viewBox={`0 0 ${gridWidth} ${gridHeight}`}
      style={{ background: 'transparent', userSelect: 'none' }}
    >
      {/* Row axis labels (left side) */}
      {ANTI_DETECTION_SIGNAL_CATALOG.map((sig, i) => (
        <text
          key={`row-label-${sig.key}`}
          x={AXIS_GUTTER - 8}
          y={AXIS_GUTTER + i * CELL_SIZE + CELL_SIZE / 2 + 4}
          textAnchor="end"
          fontSize={11}
          fill={highlightedIndex === i ? '#1677ff' : 'currentColor'}
          fontWeight={highlightedIndex === i ? 600 : 400}
        >
          {`${sig.bucket}${i + 1}. ${sig.label}`}
        </text>
      ))}

      {/* Column axis labels (top, rotated -45deg) */}
      {ANTI_DETECTION_SIGNAL_CATALOG.map((sig, j) => {
        const cx = AXIS_GUTTER + j * CELL_SIZE + CELL_SIZE / 2;
        const cy = AXIS_GUTTER - 8;
        return (
          <text
            key={`col-label-${sig.key}`}
            x={cx}
            y={cy}
            textAnchor="start"
            fontSize={11}
            fill={highlightedIndex === j ? '#1677ff' : 'currentColor'}
            fontWeight={highlightedIndex === j ? 600 : 400}
            transform={`rotate(-45 ${cx} ${cy})`}
          >
            {`${sig.bucket}${j + 1}. ${sig.label}`}
          </text>
        );
      })}

      {/* Cells */}
      {ANTI_DETECTION_SIGNAL_CATALOG.map((rowSig, i) =>
        ANTI_DETECTION_SIGNAL_CATALOG.map((colSig, j) => {
          const cell = lookup.get(`${rowSig.key}|${colSig.key}`);
          const r = cell?.r ?? 0;
          const fill = correlationColor(r);
          const isDiag = i === j;
          const isStrong = cell?.strong ?? false;
          const tooltipTitle = (
            <div style={{ minWidth: 200 }}>
              <div style={{ fontWeight: 600 }}>
                {rowSig.label} × {colSig.label}
              </div>
              <div style={{ marginTop: 4 }}>
                r = <code>{r.toFixed(3)}</code>
                {' · '}
                <span>{correlationBand(r)}</span>
                {isStrong ? (
                  <span
                    style={{
                      marginLeft: 6,
                      color: '#fff',
                      background: '#cf1322',
                      borderRadius: 3,
                      padding: '0 4px',
                    }}
                  >
                    strong
                  </span>
                ) : null}
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  color: 'rgba(0,0,0,0.45)',
                }}
              >
                {isDiag ? 'self-cell (no drill-down)' : 'click to drill down'}
              </div>
            </div>
          );
          return (
            <Tooltip
              key={`cell-${rowSig.key}-${colSig.key}`}
              title={tooltipTitle}
              mouseEnterDelay={0.05}
            >
              <rect
                data-testid={`cell-${rowSig.key}-${colSig.key}`}
                data-row-index={i}
                data-col-index={j}
                data-r={r}
                data-strong={isStrong ? '1' : '0'}
                x={AXIS_GUTTER + j * CELL_SIZE}
                y={AXIS_GUTTER + i * CELL_SIZE}
                width={CELL_SIZE - 2}
                height={CELL_SIZE - 2}
                fill={fill}
                stroke={isStrong ? '#fff' : 'transparent'}
                strokeWidth={isStrong ? 1.5 : 0}
                style={{
                  cursor: isDiag ? 'default' : 'pointer',
                  opacity:
                    highlightedIndex == null ||
                    highlightedIndex === i ||
                    highlightedIndex === j
                      ? 1
                      : 0.35,
                  transition: 'opacity 120ms ease',
                }}
                onMouseEnter={() => onHighlightChange?.(i)}
                onMouseLeave={() => onHighlightChange?.(null)}
                onClick={() => {
                  if (isDiag) return;
                  onCellClick?.(rowSig.key, colSig.key, r);
                }}
              />
            </Tooltip>
          );
        })
      )}
    </svg>
  );
};

export interface CorrelationLegendProps {
  strongPairCount: number;
  isolatedSignalCount: number;
}

export const CorrelationLegend: React.FC<CorrelationLegendProps> = ({
  strongPairCount,
  isolatedSignalCount,
}) => {
  const samples: Array<{ label: string; r: number }> = [
    { label: '|r| ≥ 0.7 strong (positive)', r: 0.85 },
    { label: '|r| ≥ 0.7 strong (inverse)', r: -0.85 },
    { label: '0.5 ≤ |r| < 0.7 medium', r: 0.6 },
    { label: '0.3 ≤ |r| < 0.5 weak', r: 0.4 },
    { label: '|r| < 0.3 noise', r: 0.1 },
  ];
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 600 }}>
        Strong pairs (|r| &gt; 0.7): {strongPairCount} · Isolated signals:{' '}
        {isolatedSignalCount}/16
      </div>
      {samples.map((s) => (
        <div
          key={s.label}
          style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 18,
              height: 18,
              background: correlationColor(s.r),
              border:
                Math.abs(s.r) > 0.7
                  ? '1.5px solid #fff'
                  : '1px solid rgba(0,0,0,0.1)',
              borderRadius: 3,
            }}
          />
          <span>{s.label}</span>
        </div>
      ))}
    </div>
  );
};

/** Re-export for the parent page so it does not need a second import. */
export type { AntiDetectionSignalSpec };
